// Unpublished Work © 2023-2024 Deere & Company.
import { useToastProvider } from "@deere/fuel-react";
import DownloadIcon from "@mui/icons-material/Download";
import { Button, Tooltip } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { useOktaAuth } from "@okta/okta-react";
import { AxiosResponse } from "axios";
import { useContext, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  IJobRegistrationResponse,
  IJobRegistrationResponseV2,
} from "../../../../api/jobsService/IJobsService";
import { ConfigContext } from "../../../../context/ConfigContext";
import {
  ALL_ACCOUNTS,
  PROMOTION_PARTS_DETAIL_DOWNLOAD,
} from "../../../../utils/constants";
import downloadFile, { BlobResponse } from "../../../../utils/downloadFile";
import {
  DOWNLOAD_JOB_ADDED,
  DOWNLOAD_NEW_JOB_ADDED,
} from "../../../../utils/eventConstants";
import getFileTypeIcon from "../../../../utils/fileIcons";
import {
  addPendingDownloadJobs,
  PendingCompilationJob,
} from "../../../../utils/notificationsUtils";
import {
  addPendingPromotionDownloadJobs,
  PendingPromotionCompilationJob,
} from "../../../../utils/promotions/promotionUtils";

export type PromotionCompileJobResponse = {
  promotionId: string;
  accountId: string;
  jobId: string;
};

interface DownloadButtonProps {
  downloadFunction: (
    accessToken: string,
    lang?: string,
    objTypeCode?: string,
    fileName?: string
  ) => Promise<
    AxiosResponse<
      BlobResponse | PendingPromotionCompilationJob | IJobRegistrationResponse
    >
  >;
  shouldDisableDownloadButton: boolean;
  shouldDisableTooltip?: boolean;
  setShowUnauthorizedError: (value: boolean) => void;
  showAuthenticateError?: boolean;
  buttonTooltip?: string;
  buttonInProgressTooltip?: string;
  buttonText?: string;
  buttonInProgressText?: string;
  isRealTimeDownload?: boolean;
  downloadInProgress?: boolean;
  variant?: "primary" | "secondary" | "text-inline" | "text";
  isAttachmentButton?: boolean;
  objTypeCode?: string;
  fileName?: string;
  showFileTypeIcon?: boolean;
  onSuccess?: () => void;
}

const DownloadButton = (props: {
  downloadButtonProps: DownloadButtonProps;
}) => {
  const {
    downloadButtonProps: {
      downloadFunction,
      shouldDisableDownloadButton,
      shouldDisableTooltip,
      setShowUnauthorizedError,
      showAuthenticateError,
      buttonText,
      buttonInProgressText,
      buttonTooltip,
      buttonInProgressTooltip,
      isRealTimeDownload,
      downloadInProgress,
      variant = "secondary",
      isAttachmentButton,
      objTypeCode,
      fileName,
      showFileTypeIcon = false,
      onSuccess,
    },
  } = props;
  const { enqueueToast } = useToastProvider();
  const { authState } = useOktaAuth();
  const theme = useTheme();
  const { t, i18n } = useTranslation("common");
  const [isDownloadDisabled, setIsDownloadDisabled] = useState<boolean>(false);
  const DOWNLOADING_TEXT =
    buttonInProgressText || t("table.download.progress.text");
  const DOWNLOAD_TEXT = buttonText || t("table.download.text");

  const DOWNLOADING_TOOLTIP =
    buttonInProgressTooltip || t("table.download.progress.tooltip");
  const DOWNLOAD_TOOLTIP = buttonTooltip || t("table.download.tooltip");

  const { ENABLE_INVOICE_EXCEL_DOWNLOAD_V2 } = useContext(ConfigContext);

  const handleDownload = () => {
    setIsDownloadDisabled(true);

    const handleError = (error: any) => {
      const authenticationError =
        error?.response?.data?.statusCode === 401 ||
        error?.response?.status === 401;
      if (
        authenticationError ||
        error?.response?.data?.statusCode === 403 ||
        error?.response?.status === 403
      ) {
        setShowUnauthorizedError(true);
        if (authenticationError && showAuthenticateError)
          enqueueToast({
            variant: "error",
            message: t("table.download.authenticationError"),
            isCloseable: true,
          });
      } else if (isRealTimeDownload) {
        enqueueToast({
          variant: "error",
          message: t("table.download.error"),
          isCloseable: true,
        });
      } else {
        enqueueToast({
          variant: "error",
          message: t("table.download.fileCompilationError"),
          isCloseable: true,
        });
      }
      setIsDownloadDisabled(false);
    };

    const handleRealTimeResponse = (response: AxiosResponse<BlobResponse>) => {
      downloadFile(response);
      setIsDownloadDisabled(false);
    };

    const handleAsyncResponse = (
      response: AxiosResponse<
        | PromotionCompileJobResponse
        | IJobRegistrationResponse
        | IJobRegistrationResponseV2
      >
    ) => {
      if (response.data) {
        const responseData = response.data as PromotionCompileJobResponse;
        if (responseData.promotionId) {
          const newJobs: PendingPromotionCompilationJob[] = [
            { ...responseData, createdAt: Date.now() },
          ];
          addPendingPromotionDownloadJobs(responseData.accountId, newJobs);
          window.dispatchEvent(
            new CustomEvent(DOWNLOAD_JOB_ADDED, { bubbles: true })
          );
        } else {
          if (
            ENABLE_INVOICE_EXCEL_DOWNLOAD_V2 &&
            "accountIds" in response.data
          ) {
            const jobResponseData = response.data as IJobRegistrationResponseV2;
            const newCompileJobs = [
              {
                jobId: jobResponseData.jobId,
                accountId: jobResponseData.accountIds.join(","),
                createdAt: Date.now(),
                type: PROMOTION_PARTS_DETAIL_DOWNLOAD,
              },
            ];
            addPendingDownloadJobs(
              jobResponseData.accountIds.length > 1
                ? ALL_ACCOUNTS
                : jobResponseData.accountIds[0],
              newCompileJobs
            );
          } else {
            const jobResponseData = response.data as IJobRegistrationResponse;
            const newCompileJobs: PendingCompilationJob[] = [
              {
                ...jobResponseData,
                createdAt: Date.now(),
                type: PROMOTION_PARTS_DETAIL_DOWNLOAD,
              },
            ];
            addPendingDownloadJobs(responseData.accountId, newCompileJobs);
          }
          window.dispatchEvent(
            new CustomEvent(DOWNLOAD_NEW_JOB_ADDED, { bubbles: true })
          );
        }
        enqueueToast({
          variant: "info",
          message: t("table.download.fileCompilationInfo"),
          isCloseable: true,
        });
      }
      setIsDownloadDisabled(false);
    };

    if (authState?.accessToken) {
      downloadFunction(
        authState.accessToken.accessToken,
        i18n.language,
        objTypeCode,
        fileName
      )
        .then((response) =>
          isRealTimeDownload
            ? handleRealTimeResponse(response as AxiosResponse<BlobResponse>)
            : handleAsyncResponse(
                response as AxiosResponse<PromotionCompileJobResponse>
              )
        )
        .then(() => {
          if (isRealTimeDownload && onSuccess) {
            onSuccess();
          }
        })
        .catch(handleError);
    }
  };

  return (
    <Tooltip
      disableHoverListener={shouldDisableTooltip}
      data-testid="download-all-button"
      sx={{ textAlign: "center", maxWidth: "210px" }}
      title={
        isDownloadDisabled || downloadInProgress
          ? (DOWNLOADING_TOOLTIP as string)
          : (DOWNLOAD_TOOLTIP as string)
      }
    >
      <span>
        <Button
          data-testid="download-button"
          sx={{
            width: "flex",
            color: isAttachmentButton
              ? theme.tokens.FuelColorJdGreen
              : theme.tokens.FuelColorGray900,
            ...(isAttachmentButton &&
              !isDownloadDisabled &&
              !(variant === "text") && {
                "&:hover": {
                  "& .MuiSvgIcon-root": {
                    color: theme.tokens.FuelColorGray900,
                  },
                },
              }),
          }}
          variant={variant}
          startIcon={
            showFileTypeIcon && buttonText ? (
              getFileTypeIcon(buttonText)
            ) : (
              <DownloadIcon
                data-testid="download-button-icon"
                sx={{
                  color: isAttachmentButton
                    ? theme.tokens.FuelColorJdGreen
                    : theme.tokens.FuelColorGray900,
                }}
              />
            )
          }
          disabled={
            isDownloadDisabled ||
            shouldDisableDownloadButton ||
            downloadInProgress
          }
          onClick={handleDownload}
        >
          {isDownloadDisabled || downloadInProgress
            ? DOWNLOADING_TEXT
            : DOWNLOAD_TEXT}
        </Button>
      </span>
    </Tooltip>
  );
};

export default DownloadButton;
